import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import './CategoryForm.css';

const schema = yup.object({
  name: yup.string()
    .required('Category name is required')
    .min(2, 'Name must be at least 2 characters')
    .max(100, 'Name must be less than 100 characters'),
  slug: yup.string()
    .required('Slug is required')
    .matches(/^[a-z0-9-]+$/, 'Slug can only contain lowercase letters, numbers, and hyphens')
    .max(100, 'Slug must be less than 100 characters'),
  description: yup.string()
    .required('Description is required')
    .min(10, 'Description must be at least 10 characters')
    .max(500, 'Description must be less than 500 characters'),
  icon: yup.string()
    .max(10, 'Icon must be less than 10 characters')
    .nullable(),
  isActive: yup.boolean(),
  displayOrder: yup.number()
    .min(0, 'Display order cannot be negative')
    .integer('Display order must be a whole number')
    .typeError('Must be a number'),
}).required();

function CategoryForm({ category, onSubmit, isSubmitting }) {
  const isEditing = !!category;

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      name: category?.name || '',
      slug: category?.slug || '',
      description: category?.description || '',
      icon: category?.icon || '',
      isActive: category?.isActive ?? true,
      displayOrder: category?.displayOrder ?? 0,
    },
  });

  const name = watch('name');
  const description = watch('description');

  // Auto-generate slug from name
  const handleNameChange = (e) => {
    const value = e.target.value;
    setValue('name', value);

    // Only auto-generate slug if creating new category or slug is empty
    if (!isEditing || !watch('slug')) {
      const slug = value
        .toLowerCase()
        .replace(/[^a-z0-9\s-]/g, '')
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-')
        .replace(/^-|-$/g, '');
      setValue('slug', slug);
    }
  };

  return (
    <form className="category-form" onSubmit={handleSubmit(onSubmit)}>
      <div className="form-grid">
        <div className="form-column">
          {/* Name */}
          <div className="form-group">
            <label htmlFor="name" className="form-label required">
              Category Name
            </label>
            <input
              id="name"
              type="text"
              className={`form-input ${errors.name ? 'error' : ''}`}
              placeholder="e.g., Football, Cricket, Rugby"
              value={name}
              onChange={handleNameChange}
            />
            {errors.name && <span className="form-error">{errors.name.message}</span>}
          </div>

          {/* Slug */}
          <div className="form-group">
            <label htmlFor="slug" className="form-label required">
              Slug
            </label>
            <input
              id="slug"
              type="text"
              className={`form-input readonly ${errors.slug ? 'error' : ''}`}
              placeholder="e.g., football-memorabilia"
              readOnly
              {...register('slug')}
            />
            {errors.slug && <span className="form-error">{errors.slug.message}</span>}
            {!errors.slug && (
              <span className="form-hint">
                Used in URLs. Auto-generated from name.
              </span>
            )}
          </div>

          {/* Description */}
          <div className="form-group">
            <label htmlFor="description" className="form-label required">
              Description
            </label>
            <textarea
              id="description"
              className={`form-textarea ${errors.description ? 'error' : ''}`}
              placeholder="Brief description of this category (10-500 characters)..."
              rows={3}
              maxLength={500}
              {...register('description')}
            />
            {errors.description && <span className="form-error">{errors.description.message}</span>}
            {!errors.description && (
              <span className="form-hint">
                {description?.length || 0}/500 characters (minimum 10)
              </span>
            )}
          </div>

          {/* Icon (Emoji) */}
          <div className="form-group">
            <label htmlFor="icon" className="form-label">
              Icon
            </label>
            <input
              id="icon"
              type="text"
              className={`form-input ${errors.icon ? 'error' : ''}`}
              placeholder="⚽"
              {...register('icon')}
            />
            {errors.icon && <span className="form-error">{errors.icon.message}</span>}
            {!errors.icon && (
              <span className="form-hint">
                Enter an emoji (⚽ 🏏 🏉 🏀 🎾)
              </span>
            )}
          </div>

          {/* Display Order */}
          <div className="form-group">
            <label htmlFor="displayOrder" className="form-label">
              Display Order
            </label>
            <input
              id="displayOrder"
              type="number"
              className={`form-input ${errors.displayOrder ? 'error' : ''}`}
              placeholder="0"
              min="0"
              {...register('displayOrder')}
            />
            {errors.displayOrder && <span className="form-error">{errors.displayOrder.message}</span>}
            {!errors.displayOrder && (
              <span className="form-hint">
                Lower numbers appear first. Use this to control category order.
              </span>
            )}
          </div>

          {/* Active Status */}
          <div className="form-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                className="form-checkbox"
                {...register('isActive')}
              />
              <span>Active (visible to users)</span>
            </label>
            <span className="form-hint">
              Inactive categories won't be shown to customers
            </span>
          </div>
        </div>
      </div>

      {/* Form Actions */}
      <div className="form-actions">
        <button
          type="submit"
          className="btn btn-primary"
          disabled={isSubmitting}
        >
          {isSubmitting ? (
            <>
              <svg className="animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Saving...
            </>
          ) : (
            <>Save Category</>
          )}
        </button>
      </div>
    </form>
  );
}

export default CategoryForm;
